import React from 'react';
import { faUserDoctor } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/doctor-dog.jpg
import doctorDogImg from '../../../assets/images/blogs/doctor-dog.jpg';

const DoctorDog = () => (
    <BlogPost
        title="Doctor Dog: Building ConsistentAI Agents"
        subtitle="AI agents that 'discover, reason, and act' sound great - until you need to trust them with clinical decisions."
        category="Architecture"
        categoryIcon={faUserDoctor}
        color="#003366"
        date="February 2026"
        readTime="6 min read"
        image={doctorDogImg}
        imageAlt="Doctor Dog - ConsistentAI agents for clinical decision support"
        ctaHeading="Want ConsistentAI for your domain?"
        ctaText="Book a discovery call and we'll show you how AgentDOG delivers trustworthy AI decisions with full auditability."
        seoDescription="How Doctor Dog demonstrates ConsistentAI agents for clinical decision support using the AgentDOG framework with semantic reasoning and full provenance."
        seoKeywords="ConsistentAI, clinical AI, AgentDOG, AI agents, healthcare AI, decision support, Nebulyx"
        slug="doctor-dog"
    >
        <p>
            AI agents that "discover, reason, and act" sound great - until you need to trust
            them with clinical decisions.
        </p>

        <p>
            That's the challenge I decided to tackle with Doctor Dog, a clinical decision
            support system using Consistent(powerful and predictable) AI Agents, using
            our AgentDOG framework.
        </p>

        <h3>1. Semantic Reasoning Engine</h3>

        <p>
            Traditional knowledge graphs define meaning at design time. Experts curate
            concepts and relationships - valuable, but slow. Edge cases appear faster than
            governance can respond.
        </p>

        <p>
            I took a different path and used <strong>AI-native semantics</strong>.
        </p>

        <p>
            Meaning is extracted dynamically at runtime, not locked in at design time. The
            system consumes domain knowledge, clinical guidelines, and real-world data, then
            encodes it as graphs and embeddings that AI models reason over directly.
        </p>

        <p>
            This difference matters - e.g. when a new drug interaction emerges or guidelines
            update, the system adapts. It discovers what it needs to know, not just what it
            already knows.
        </p>

        <p>
            Context-aware processing means "exposure" in trading differs from "exposure" in
            clinical practice - multiple frames exist, the right one activates.
        </p>

        <h3>2. Learnable Execution Policy</h3>

        <p>
            We don't hardcode every pathway. The agent learns from real scenarios - UTI
            presentations, contraindication patterns, red flags.
        </p>

        <p>
            First, we extract the foundational state space: core actions, patterns, and
            workflows. Then we run actual transactional data to learn operational differences
            between textbook medicine and real-world practice. This uncovers hidden states
            and edge cases.
        </p>

        <p>
            The result is a <strong>runtime policy</strong> - rules of the road for agent
            execution.
        </p>

        <blockquote className="blog-post-quote">
            "When the agent sees 'elderly female, eGFR 30, dysuria,' it knows nitrofurantoin
            is contraindicated for renal impairment and selects trimethoprim instead. Not
            because we wrote an if-statement, but because it learned the pattern - grounded
            in the domain literature."
        </blockquote>

        <h3>3. State &amp; Context Manager</h3>

        <p>
            Every query, every decision, every reasoning step is captured as a Context State
            Graph (implemented as a Data Object Graph) - grounded in current reality but with
            full provenance.
        </p>

        <p>
            When the agent recommends a treatment, you can trace exactly why: which features
            matched, which contraindications fired, which policy drove the decision.
            <strong> Auditable and explainable.</strong>
        </p>

        <h3>An Example</h3>

        <p>
            A 70-year-old patient presents with burning urination. The agent:
        </p>

        <p>
            • Extracts structured data from the query<br />
            • Derives features (elderly, female, renal impaired)<br />
            • Checks contraindications against her eGFR<br />
            • Runs the learned policy<br />
            • Returns a specific recommendation with dosing, warnings, and complete reasoning
        </p>

        <p>
            All in real-time - but Consistentand trustworthy.
        </p>

        <h3>The Future of Agentic AI</h3>

        <p>
            This is the future of Agentic AI with use-cases like assisted healthcare - quality
            and AI-native decisioning solutions that are deterministic, accessible, efficient
            and trusted while keeping humans firmly in control. 🎯
        </p>
    </BlogPost>
);

export default DoctorDog;
